import React, { useState, useEffect, useRef, RefObject } from "react";
import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./Layout.module.css";

import { useLogin } from "../../authConfig";

import { LoginButton } from "../../components/LoginButton";
import { IconButton, Modal, DefaultButton, Icon } from "@fluentui/react";

import logo from "../../assets/logo.png";
import ieoLogo from "../../assets/IEO-logo-SVG-Blue.svg";

const Layout = () => {
    const { t } = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [isPrivacyPolicyModalOpen, setIsPrivacyPolicyModalOpen] = useState(false);
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [isQualtricsFeedbackModalOpen, setIsQualtricsFeedbackModalOpen] = useState(false);
    const [hasAutoPopupShown, setHasAutoPopupShown] = useState<boolean>(false);
    const qualtricsTimerRef = useRef<NodeJS.Timeout>();
    const menuRef: RefObject<HTMLDivElement> = useRef(null);
    const navigate = useNavigate();
    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    const surveyUrl = "https://undpieo.qualtrics.com/jfe/form/SV_1RGO1PQtNitBadg";

    // Handle feedback button click
    const handleFeedbackClick = () => {
        setIsFeedbackModalOpen(true);
        setHasAutoPopupShown(true);
    };

    // Qualtrics survey auto-popup timer
    useEffect(() => {
        if (!hasAutoPopupShown) {
            qualtricsTimerRef.current = setTimeout(() => {
                setIsFeedbackModalOpen(true);
                setHasAutoPopupShown(true);
            }, 3600000); // 60 minutes
        }

        return () => {
            if (qualtricsTimerRef.current) {
                clearTimeout(qualtricsTimerRef.current);
            }
        };
    }, [hasAutoPopupShown]);

    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setMenuOpen(false);
        }
    };

    useEffect(() => {
        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuOpen]);

    return (
        <div className={styles.layout}>
            <header className={styles.header} role={"banner"}>
                <div className={styles.headerContainer} ref={menuRef}>
                    <Link to="https://www.undp.org/evaluation" target="_blank" className={styles.headerTitleContainer}>
                        <div className={styles.headerLogoWrapper}>
                            <img src={ieoLogo} alt="IEO" className={styles.headerLogoImage} />
                        </div>
                    </Link>
                    <div className={styles.loginMenuContainer}>
                        <nav>
                            <ul className={`${styles.headerNavList} ${menuOpen ? styles.show : ""}`}>
                                <li className={styles.headerNavItemHome}>
                                    <NavLink
                                        to="/"
                                        className={({ isActive }) => (isActive ? styles.headerNavPageLinkActive : styles.headerNavPageLink)}
                                        onClick={e => {
                                            e.preventDefault();
                                            setMenuOpen(false);
                                            navigate("/");
                                            window.dispatchEvent(new CustomEvent("aida:home"));
                                        }}
                                    >
                                        HOME
                                    </NavLink>
                                </li>
                                <li className={styles.headerNavItemAbout}>
                                    <NavLink
                                        to="/about"
                                        className={({ isActive }) => (isActive ? styles.headerNavPageLinkActive : styles.headerNavPageLink)}
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        ABOUT
                                    </NavLink>
                                </li>
                                {/* [HIDDEN CONCEPT PAGE] To re-enable Resource page, uncomment the <li> block below:
                                <li className={styles.headerNavItemResource}>
                                    <NavLink
                                        to="/resource"
                                        className={({ isActive }) => (isActive ? styles.headerNavPageLinkActive : styles.headerNavPageLink)}
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        RESOURCE
                                    </NavLink>
                                </li>
                                */}
                            </ul>
                        </nav>
                        {useLogin && <LoginButton />}
                        <IconButton
                            iconProps={{ iconName: "GlobalNavButton" }}
                            className={styles.menuToggle}
                            onClick={toggleMenu}
                            ariaLabel={t("labels.toggleMenu")}
                        />
                    </div>
                </div>
            </header>

            <Outlet />

            {/* Feedback button */}
            <div className={styles.feedbackButtonContainer}>
                <button onClick={handleFeedbackClick} className={styles.feedbackButton} title="Give Feedback" aria-label="Give Feedback">
                    <Icon iconName="Feedback" className={styles.feedbackIcon} />
                    Feedback
                </button>
            </div>

            {/* Data Privacy Policy Link */}
            <div className={styles.privacyPolicyContainer}>
                <span className={styles.privacyPolicyLink} onClick={() => setIsPrivacyPolicyModalOpen(true)}>
                    <Icon iconName="Info" className={styles.privacyPolicyIcon} />
                    Data Privacy Policy
                </span>
            </div>

            <Modal
                isOpen={isPrivacyPolicyModalOpen}
                onDismiss={() => setIsPrivacyPolicyModalOpen(false)}
                isBlocking={false}
                containerClassName={styles.privacyPolicyModal}
            >
                <div className={styles.modalBackdrop} onClick={() => setIsPrivacyPolicyModalOpen(false)}>
                    <div className={styles.privacyPolicyModalContent} onClick={e => e.stopPropagation()}>
                    <h2 className={styles.privacyPolicyTitle}>Data Privacy Policy</h2>
                    <div className={styles.privacyPolicyDivider}></div>
                    <div className={styles.privacyPolicyModalBody}>
                        <p>This platform is under active development. It should not be relied upon in business-critical scenarios.</p>
                        <ul>
                            <li>
                                All data provided by you via the platform will be securely stored and processed using UNDP's infrastructure on Microsoft
                                Azure.
                            </li>
                            <li>Any messages, documents, or data provided in this platform will NOT be used to pre-train or fine-tune models.</li>
                            <li>Any chats or prompts created during this phase are not guaranteed to persist.</li>
                        </ul>
                        <p>
                            To improve our products, we analyze how visitors interact with our website. By continuing to use our site, you agree that we may
                            collect and use this data to enhance your experience. Our privacy statement{" "}
                            <a href="https://popp.undp.org/policy-page/personal-data-protection-and-privacy-policy">
                                https://popp.undp.org/policy-page/personal-data-protection-and-privacy-policy
                            </a>{" "}
                            has more details.
                        </p>
                    </div>
                    <button className={styles.privacyPolicyCloseButton} onClick={() => setIsPrivacyPolicyModalOpen(false)}>
                        CLOSE
                    </button>
                    </div>
                </div>
            </Modal>

            {/* Custom Feedback Modal */}
            <Modal
                isOpen={isFeedbackModalOpen}
                onDismiss={() => setIsFeedbackModalOpen(false)}
                isBlocking={false}
                containerClassName={styles.customFeedbackModal}
            >
                <div className={styles.modalBackdrop} onClick={() => setIsFeedbackModalOpen(false)}>
                    <div className={styles.customFeedbackModalContent} onClick={e => e.stopPropagation()}>
                        <button
                            className={styles.feedbackCloseX}
                            onClick={() => setIsFeedbackModalOpen(false)}
                            aria-label="Close feedback"
                        >
                            &times;
                        </button>
                        <div className={styles.customFeedbackModalBody}>
                        <h2 className={styles.feedbackQuestion}>Are you finding AIDA useful?</h2>
                        <div className={styles.feedbackDivider}></div>
                        <div className={styles.feedbackOptions}>
                            <label className={styles.feedbackOptionLabel}>
                                <input type="radio" name="feedback" className={styles.feedbackRadio} />
                                <span className={styles.feedbackCircle}></span>
                                <span className={styles.feedbackOptionText}>Yes</span>
                            </label>
                            <label className={styles.feedbackOptionLabel}>
                                <input type="radio" name="feedback" className={styles.feedbackRadio} />
                                <span className={styles.feedbackCircle}></span>
                                <span className={styles.feedbackOptionText}>No</span>
                            </label>
                        </div>
                        <label className={styles.feedbackTextareaLabel}>Any feedback for us?</label>
                        <textarea
                            className={styles.feedbackTextarea}
                            rows={4}
                        />
                        <div className={styles.feedbackActions}>
                            <button className={styles.feedbackSubmit} onClick={() => setIsFeedbackModalOpen(false)}>
                                SUBMIT
                            </button>
                        </div>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Qualtrics Survey Modal (default settings) */}
            <Modal
                isOpen={isQualtricsFeedbackModalOpen}
                onDismiss={() => setIsQualtricsFeedbackModalOpen(false)}
                isBlocking={false}
                containerClassName={styles.qualtricsFeedbackModal}
            >
                <div className={styles.modalBackdrop} onClick={() => setIsQualtricsFeedbackModalOpen(false)}>
                    <div className={styles.qualtricsFeedbackModalContent} onClick={e => e.stopPropagation()}>
                        <iframe
                            src={surveyUrl}
                            title="Feedback Survey"
                            className={styles.qualtricsFeedbackIframe}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Layout;
